import { supabase } from './supabase'

export type UserStatus = 'pending' | 'approved' | 'rejected'
export type UserRole = 'admin' | 'user'

export interface AppUser {
  id: string
  email: string
  name: string | null
  avatar_url: string | null
  status: UserStatus
  role: UserRole
  first_seen: string
  last_seen: string
}

/**
 * Kick off Google OAuth. After Google login, the user is redirected back
 * to the current site origin, where detectSessionInUrl completes the login.
 */
export async function signInWithGoogle(): Promise<void> {
  await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: window.location.origin,
      queryParams: { prompt: 'select_account' },
    },
  })
}

export async function signOut(): Promise<void> {
  await supabase.auth.signOut()
}

/**
 * Given a freshly-authenticated Supabase user (email/name/avatar), ensure a row
 * exists in app_users, then return their CURRENT record (true status/role).
 *
 * Design notes (avoids a race-condition bug where two parallel calls during
 * login both tried to INSERT and one returned a spurious 'pending'):
 *  1. Read the existing row FIRST. If it exists, that is the source of truth for
 *     status/role — we NEVER overwrite those here (only last_seen/name/avatar).
 *  2. Only INSERT (as 'pending') if no row exists. Ignore duplicate-key errors
 *     (a concurrent call may have inserted first).
 *  3. Always re-read at the end so the returned status/role is authoritative,
 *     regardless of which concurrent call ran.
 */
export async function syncAppUser(authUser: {
  email: string
  name?: string | null
  avatar_url?: string | null
}): Promise<AppUser | null> {
  const email = authUser.email.toLowerCase()

  // 1. Read existing row (maybeSingle → null instead of error when absent)
  const { data: existing } = await supabase
    .from('app_users')
    .select('*')
    .eq('email', email)
    .maybeSingle()

  if (existing) {
    // Only touch last_seen / name / avatar — NEVER status or role
    await supabase
      .from('app_users')
      .update({
        last_seen: new Date().toISOString(),
        name: authUser.name ?? existing.name,
        avatar_url: authUser.avatar_url ?? existing.avatar_url,
      })
      .eq('email', email)
  } else {
    // No row yet → create as pending. Ignore duplicate-key errors from a
    // concurrent insert; we re-read below regardless.
    await supabase
      .from('app_users')
      .insert({
        email,
        name: authUser.name ?? null,
        avatar_url: authUser.avatar_url ?? null,
        status: 'pending',
        role: 'user',
      })
  }

  // 3. Authoritative re-read — this is what decides access.
  // Retry a few times: right after login the client may not have attached the
  // JWT to the request yet, so the first read can hit RLS as anon and return null.
  for (let attempt = 0; attempt < 4; attempt++) {
    const { data: current } = await supabase
      .from('app_users')
      .select('*')
      .eq('email', email)
      .maybeSingle()
    if (current) return current as AppUser
    // brief backoff before retry
    await new Promise(r => setTimeout(r, 300))
  }

  return null
}
